import { useState, useEffect } from 'react'
import { Button, Dialog, DialogContent, DialogActions, DialogTitle, IconButton, CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { gettingInterpretationRuleById } from 'utils/InterpretationPunchRule'
import { InterpretationRuleForm } from 'components/dialog/interpretation_rules/InterpretationRuleForm'
import type { InterpretationRuleFormData, InterpretationRuleDurations } from 'components/dialog/interpretation_rules/InterpretationRuleForm'

interface InterpretationRuleRow {
    id: number
}

interface InfoInterpretationRuleProps {
    open: boolean
    setOpen: (value: boolean) => void
    selectedRow: InterpretationRuleRow[]
    cleanSelected: () => void
}

const defaultDurations: InterpretationRuleDurations = {
    early_start_margin: '',
    early_start_restriction: '',
    late_start_restriction: '',
    late_start_margin: '',
    omitted_output_margin: '',
    brake_maximum_output: '',
    brake_minimum_refreshment_: '',
    brake_early_rest_start: '',
    brake_late_rest_start_margin: '',
    brake_late_rest_end_margin: '',
    start_early_end_restriction: '',
    end_early_end_restriction: '',
    late_end_restriction: '',
    punch_out_limit_omitted: '',
    unplanned_shift_duration: '',
}

const InfoInterpretationRule = ({ open, setOpen, selectedRow, cleanSelected }: InfoInterpretationRuleProps) => {

    const { t } = useTranslation()
    const [formData, setFormData] = useState<InterpretationRuleFormData>({
        name: '',
        description: '',
        punch_restriction: '',
        allow_substitution: null,
        brake_impose_reset: null,
        restrict_unplanned_punch_in: null,
        durations: defaultDurations,
    })
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (open && selectedRow?.length > 0) {
            setLoading(true)
            gettingInterpretationRuleById(setLoading, selectedRow[0].id)
                .then((response: any) => {
                    setFormData({
                        name: response.name,
                        description: response.description,
                        punch_restriction: response.punch_restriction,
                        allow_substitution: response.allow_substitution,
                        brake_impose_reset: response.brake_impose_reset,
                        restrict_unplanned_punch_in: response.restrict_unplanned_punch_in,
                        durations: {
                            early_start_margin: response.early_start_margin,
                            early_start_restriction: response.early_start_restriction,
                            late_start_restriction: response.late_start_restriction,
                            late_start_margin: response.late_start_margin,
                            omitted_output_margin: response.omitted_output_margin,
                            brake_maximum_output: response.brake_maximum_output,
                            brake_minimum_refreshment_: response.brake_minimum_refreshment_,
                            brake_early_rest_start: response.brake_early_rest_start,
                            brake_late_rest_start_margin: response.brake_late_rest_start_margin,
                            brake_late_rest_end_margin: response.brake_late_rest_end_margin,
                            start_early_end_restriction: response.start_early_end_restriction,
                            end_early_end_restriction: response.end_early_end_restriction,
                            late_end_restriction: response.late_end_restriction,
                            punch_out_limit_omitted: response.punch_out_limit_omitted,
                            unplanned_shift_duration: response.unplanned_shift_duration,
                        },
                    })
                })
                .catch((error: unknown) => {
                    console.error('Error:', error)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [open, selectedRow])

    const closeForm = () => {
        setFormData({
            name: '',
            description: '',
            punch_restriction: '',
            allow_substitution: null,
            brake_impose_reset: null,
            restrict_unplanned_punch_in: null,
            durations: defaultDurations,
        })
        setLoading(false)
        setOpen(false)
        cleanSelected()
    }


    return (
        <Dialog
            open={open}
            onClose={() => closeForm()}
            maxWidth='sm'
            fullWidth
        >
            <DialogTitle>{t('settings.interpretation_rule.dialog.titles.info')}</DialogTitle>
            <IconButton
                aria-label='close'
                onClick={() => closeForm()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                {
                    loading ? (
                        <Box sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <InterpretationRuleForm
                            readOnly
                            formData={formData}
                        />
                    )
                }

            </DialogContent>
            <DialogActions>
                <Button onClick={() => closeForm()} variant='contained' color='error' loading={loading}>
                    {t('general.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { InfoInterpretationRule }
